/**
 * The bump panel type and the settings it draws from.
 */

const bump = {
  names: [
    {
      id: 'bump',
      label: 'Bump chart',
      phrase: 'a bump chart',
      guide: 'bump.md',
      advanced: true,
      toolSummary: 'who moved up and who moved down in a ranking, period by period — one row per series per period, up to 8 series across 12 periods',
      promptSummary: 'who moved up and who moved down in a ranking, period by period: every series is a line whose height is its POSITION rather than its value, so each crossing is an overtake and no two marks ever sit on top of each other — one row per series per period, up to 8 series across 12 periods; when the sizes matter more than the order, a line chart is the type instead',
      /* The plot runs wide: the periods spread across it and the series names sit in a gutter at
         each end. The type asks for that shape once and stays freely resizable after. */
      creationLayout: { w: 8, h: 7 }
    }
  ],
  block: 'bump',
  /* Bump. The query already returns one row per series per period; this block identifies the period,
     the series, and the one thing the position is taken from. That last part is either a value the
     panel ranks or a rank the source already carries, which is a rule about two fields at once and
     so lives in `validate` rather than on either field. There is no field for the axis or the
     colours: the axis runs from rank 1 down to the last rank drawn, and the colour comes from the
     series' position in the shared palette. */
  fields: {
    periodKey: { kind: 'string', required: true, hint: 'a position needs the period it was held in and the series that held it', label: 'Period' },
    seriesKey: { kind: 'string', required: true, hint: 'a position needs the period it was held in and the series that held it', label: 'Series' },
    valueKey: { kind: 'string', clearable: true, label: 'Value' },
    rankKey: { kind: 'string', clearable: true, label: 'Rank' },
    /* Only means anything when the panel does the ranking; a rank column already says which end is
       first. */
    order: { kind: 'enum', values: ['highest', 'lowest'], default: 'highest', label: 'First place', ariaLabel: 'Which value takes first place', valueLabels: { highest: 'Highest is 1st', lowest: 'Lowest is 1st' } }
  },
  validate: ({ block }) => {
    const hasValue = typeof block.valueKey === 'string' && Boolean(block.valueKey);
    const hasRank = typeof block.rankKey === 'string' && Boolean(block.rankKey);

    if (hasValue && hasRank) {
      return 'bump takes either valueKey or rankKey, never both: send valueKey to have the panel work the positions out, or rankKey when the source already carries them.';
    }

    if (!hasValue && !hasRank) {
      return 'bump needs one of valueKey or rankKey: the panel plots a position, and it either works that out from a value or reads it from a column.';
    }

    return '';
  },
  editorHint: 'One row is one series in one period. The periods run left to right in the order they first arrive, so order the query by period; the panel never reorders them. Give it either the value each series had, and it works the positions out, or a rank column when the source already carries the position. Keep the period labels short: they sit above a narrow column each.',
  /* Which of the two inputs is live is named because it decides what every mark on the panel MEANS:
     a rank column is taken as given, and a value is ranked here, from the end this says. */
  stateSummary: ({ block }) => {
    if (!block.periodKey || !block.seriesKey) {
      return '';
    }

    if (block.rankKey) {
      return `bump: ${block.periodKey} × ${block.seriesKey}, rank from ${block.rankKey}`;
    }

    return block.valueKey ? `bump: ${block.periodKey} × ${block.seriesKey}, ranked by ${block.valueKey}, ${block.order === 'lowest' ? 'lowest' : 'highest'} first` : '';
  },
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: ['periodKey', 'seriesKey'],
    description: 'Bump mapping (displayType "bump"): ONE ROW IS ONE SERIES IN ONE PERIOD, and what is plotted is the series\' POSITION in that period, not its value, so every crossing of two lines is an overtake. Send EITHER valueKey, and the panel works the positions out per period, OR rankKey when the source already carries them — never both. Prefer valueKey: a GROUP BY already returns the value, while a rank has to be built with a window function per period, and the panel owns the tie rule either way. THE ROW ORDER IS THE PERIOD ORDER — the panel never reorders, so the query must return the periods oldest first. Positions are worked out among the series this panel DRAWS, so filter to the contenders in SQL rather than sending the whole catalog. A series with no row in a period has no mark there and its line breaks across the gap. Capped at 8 series and 12 periods, with anything not drawn counted below the plot. Read immutable/analytics/panel-types/bump.md before configuring one.',
    properties: {
      periodKey: { type: 'string', description: 'Column naming the period the position was held in (a month, a week, a quarter). Its distinct values become the columns of the plot, in the order they first appear. Dot paths work. Keep the labels short: each one sits above a narrow column.' },
      seriesKey: { type: 'string', description: 'Column naming the thing being ranked. Its distinct values become the lines, and each one takes a colour from the shared palette in the order it first appears. Dot paths work.' },
      valueKey: {
        type: ['string', 'null'],
        description: 'Column holding the number the position is worked out from, for the period on the same row. Dot paths work. This is the one to send in almost every case: a GROUP BY already produces it. An empty, blank, or non-numeric value is counted below the plot rather than read as a zero. Send this OR rankKey, never both.'
      },
      rankKey: {
        type: ['string', 'null'],
        description: 'Column holding the position itself, when the source publishes one the panel could not work out (a chart position, a league table, a leaderboard endpoint). Whole numbers from 1 to 8; anything else, including a repeat of a position already taken in that period, is counted below the plot rather than drawn. Dot paths work. Send this OR valueKey, never both.'
      },
      order: {
        type: 'string',
        enum: ['highest', 'lowest'],
        description: 'Which end of the value takes first place, when the panel works the positions out. "highest" (default) for revenue, uses, signups. "lowest" for finishing times, response times, cost per lead. Ignored when rankKey is set, since a rank column already says which end is first.'
      }
    }
  }
};

export default bump;
